import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { type ReactNode, createContext, useEffect, useMemo, useState } from 'react'

import { type OptimizelyFlagKey, type OptimizelyFlagValue, OPTIMIZELY_FLAGS } from '@/types/optimizely-flags'

import { SESSION_STORAGE_KEYS } from '@/lib/storage-keys'

/** Default wait (ms) before showing content when a specific flag is not yet ready. */
export const OPTIMIZELY_DEFAULT_WAIT_MS = 2000

/** Query-param prefix that forces a flag, e.g. ?opt_HERO_TRIAL_PAYWALL=variation-1. */
const FLAG_PREFIX = 'opt_'

const VARIATION_VALUE_PATTERN = /^(original|variation-\d+)$/
const VALID_FLAG_NAMES = new Set<string>(Object.values(OPTIMIZELY_FLAGS))

type FlagOverrides = Partial<Record<OptimizelyFlagKey, OptimizelyFlagValue>>

function isValidFlagName(name: unknown): name is OptimizelyFlagKey {
  return typeof name === 'string' && VALID_FLAG_NAMES.has(name)
}

function isValidOverrideValue(value: unknown): value is OptimizelyFlagValue {
  return typeof value === 'string' && VARIATION_VALUE_PATTERN.test(value)
}

/**
 * Extracts Optimizely flag overrides from URL query params, e.g.
 * ?opt_HERO_TRIAL_PAYWALL=variation-1. The key after `opt_` is matched
 * case-insensitively against OPTIMIZELY_FLAGS keys (which are upper-cased).
 * The returned map is keyed by the resolved flag name (the OPTIMIZELY_FLAGS value).
 */
export function overridesFromQuery(query: Record<string, string | string[] | undefined>): FlagOverrides {
  const overrides: FlagOverrides = {}
  for (const [param, value] of Object.entries(query)) {
    if (!param.startsWith(FLAG_PREFIX)) continue
    const flagName = OPTIMIZELY_FLAGS[param.slice(FLAG_PREFIX.length).toUpperCase() as keyof typeof OPTIMIZELY_FLAGS]
    if (isValidFlagName(flagName) && isValidOverrideValue(value)) overrides[flagName] = value
  }
  return overrides
}

/**
 * Reads previously-persisted overrides from sessionStorage, discarding any entry
 * whose flag name or value is no longer valid (e.g. a since-removed experiment or
 * a hand-edited value). Safe on the server and when storage is unavailable (returns {}).
 */
export function readStoredOverrides(): FlagOverrides {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEYS.OPTIMIZELY_OVERRIDES)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return Object.entries(parsed).reduce<FlagOverrides>((acc, [flagName, value]) => {
      if (isValidFlagName(flagName) && isValidOverrideValue(value)) acc[flagName] = value
      return acc
    }, {})
  } catch {
    return {}
  }
}

function persistOverrides(overrides: FlagOverrides): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEYS.OPTIMIZELY_OVERRIDES, JSON.stringify(overrides))
  } catch {
    // sessionStorage can be unavailable (private mode, quota exceeded) — overrides simply won't persist.
  }
}

/**
 * Shape of a single entry returned by getCampaignStates().
 * experiment.name is the flag key (e.g. 'LOW_COST_TRIAL').
 * variation.name is the variation name (e.g. 'Original', 'Variation #1').
 */
type CampaignState = {
  experiment: { id: string; name: string }
  variation: { id: string; name: string }
  isInCampaignHoldback: boolean
}

type OptimizelyStateAPI = {
  getCampaignStates: (filter?: { isActive?: boolean }) => Record<string, CampaignState>
}

/**
 * Shape of the campaignDecided event payload relayed by _app.tsx as 'optimizelyVariationAssigned'.
 */
type CampaignDecidedData = {
  campaign: {
    name: string
    experiments: Array<{
      variations: Array<{ id: string; name: string }>
    }>
  }
  decision: {
    variationId: string
    isCampaignHoldback: boolean
  }
}

/**
 * Below 100% traffic allocation, Optimizely still buckets held-back visitors into a
 * variation but expects them to see the original and to stay out of the results.
 */
type FlagDecision = { value: OptimizelyFlagValue; isHoldback: boolean }

type FlagDecisions = Record<string, FlagDecision>

/**
 * Normalizes Optimizely dashboard variation names to internal flag values.
 * 'Original' → 'original', 'Variation #1' → 'variation-1', etc.
 */
function normalizeVariationName(name: string): OptimizelyFlagValue {
  if (name === 'Original') return 'original'
  const match = name.match(/^Variation #(\d+)$/)
  if (match) return `variation-${match[1]}` as `variation-${number}`

  Sentry.captureException(new Error(`Unrecognised Optimizely variation name: ${name}`))
  return 'original'
}

export function decisionsFromCampaignStates(campaignStates: Record<string, CampaignState>): FlagDecisions {
  return Object.values(campaignStates).reduce<FlagDecisions>((acc, { experiment, variation, isInCampaignHoldback }) => {
    if (experiment?.name && variation?.name) {
      acc[experiment.name] = {
        value: normalizeVariationName(variation.name),
        isHoldback: isInCampaignHoldback,
      }
    }
    return acc
  }, {})
}

export function decisionFromCampaignDecided(
  data: CampaignDecidedData,
): { flagKey: string; decision: FlagDecision } | null {
  const flagKey = data.campaign?.name
  const variationId = data.decision?.variationId
  const variation = data.campaign?.experiments.flatMap((exp) => exp.variations).find((v) => v.id === variationId)

  if (!flagKey || !variation?.name) return null
  return {
    flagKey,
    decision: { value: normalizeVariationName(variation.name), isHoldback: data.decision.isCampaignHoldback },
  }
}

type FlagValues = Record<string, OptimizelyFlagValue>

/**
 * `flags` drives rendering, so holdback visitors get the original. `assignments` is
 * what gets reported (attribution, Braze), so holdback visitors are left out.
 */
export function resolveFlags(
  decisions: FlagDecisions | undefined,
  overrides: FlagOverrides,
): { flags: FlagValues | undefined; assignments: FlagValues | undefined } {
  if (!decisions && Object.keys(overrides).length === 0) return { flags: undefined, assignments: undefined }

  const flags: FlagValues = {}
  const assignments: FlagValues = {}
  for (const [key, { value, isHoldback }] of Object.entries(decisions ?? {})) {
    flags[key] = isHoldback ? 'original' : value
    if (!isHoldback) assignments[key] = value
  }
  return { flags: { ...flags, ...overrides }, assignments: { ...assignments, ...overrides } }
}

export type OptimizelyContextValue = {
  flags: FlagValues | undefined
  assignments: FlagValues | undefined
  consentGiven: boolean
  setFlagOverride: (key: string, value: OptimizelyFlagValue) => void
}

export const OptimizelyContext = createContext<OptimizelyContextValue | null>(null)

type OptimizelyProviderProps = {
  children: ReactNode
  enabled?: boolean
  hasConsent?: boolean
}

export const OptimizelyProvider = ({ children, enabled = true, hasConsent = false }: OptimizelyProviderProps) => {
  const [decisions, setDecisions] = useState<FlagDecisions | undefined>(undefined)

  useEffect(() => {
    if (!enabled) return

    // Opted out of Optimizely: no flag will ever load, so resolve every experiment
    // to control immediately instead of waiting out the isReadyToShow timeout.
    const isOptedOut = typeof document !== 'undefined' && document.cookie.includes('optimizelyOptOut=true')
    if (isOptedOut) {
      const defaultDecisions = Object.values(OPTIMIZELY_FLAGS).reduce<FlagDecisions>((acc, flagKey) => {
        acc[flagKey] = { value: 'original', isHoldback: false }
        return acc
      }, {})
      setDecisions(defaultDecisions)
      return
    }

    const applyDecisions = (incoming: FlagDecisions): void => {
      if (Object.keys(incoming).length === 0) return
      setDecisions((prev) => ({ ...prev, ...incoming }))
    }

    const readCampaignStates = (): boolean => {
      const optimizelyState = window.optimizely?.get?.('state') as OptimizelyStateAPI | undefined
      if (!optimizelyState?.getCampaignStates) return false
      // Read all decided campaigns regardless of current page activation — experiments like
      // Low Cost Trial are only targeted at the plans page but their bucket assignment must
      // carry through to other pages (e.g. order confirmation after Stripe redirect).
      applyDecisions(decisionsFromCampaignStates(optimizelyState.getCampaignStates()))
      return true
    }

    // Scenario A: Optimizely ran before React mounted (render-blocking script + cached config).
    // getCampaignStates() holds decisions synchronously — read them immediately.
    const alreadyInitialized = readCampaignStates()

    // Scenario B: Optimizely's datafile loads async after React (typical on redirect back from
    // Stripe, where campaignDecided fires before _app.tsx sets up its listener).
    // Listen for 'initialized' and read getCampaignStates() at that point.
    if (!alreadyInitialized) {
      const initializedHandler = () => readCampaignStates()
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'addListener',
        filter: { type: 'lifecycle', name: 'initialized' },
        handler: initializedHandler,
      })
    }

    // Scenario C: Optimizely fires campaignDecided after React (less common).
    // _app.tsx relays campaignDecided as 'optimizelyVariationAssigned' — handle it here.
    const handleVariationAssigned = (e: Event): void => {
      const data = (e as CustomEvent<{ data: CampaignDecidedData }>).detail?.data
      const decided = data ? decisionFromCampaignDecided(data) : null
      if (decided) {
        applyDecisions({ [decided.flagKey]: decided.decision })
      }
    }

    window.addEventListener('optimizelyVariationAssigned', handleVariationAssigned)

    return () => {
      window.removeEventListener('optimizelyVariationAssigned', handleVariationAssigned)
    }
  }, [enabled])

  // Flag overrides from URL query params (e.g. ?opt_HERO_TRIAL_PAYWALL=variation-1),
  // persisted to sessionStorage so they survive client-side navigation to the page
  // under test — Next.js drops the query string on route changes, which would
  // otherwise lose the override. Set once on any page; ready on every later page in the tab.
  const router = useRouter()
  const [urlOverrides, setUrlOverrides] = useState<FlagOverrides>({})

  // Seed from any overrides persisted earlier in this tab (client-only; runs once).
  useEffect(() => {
    if (!enabled) return
    const stored = readStoredOverrides()
    if (Object.keys(stored).length > 0) setUrlOverrides(stored)
  }, [enabled])

  // Merge in overrides present in the current URL and persist them for later pages.
  // Bail out if nothing changed so we don't allocate a new object (and re-render the
  // whole app from this root provider) on every navigation that carries no override.
  useEffect(() => {
    if (!enabled || !router.isReady) return
    const fromQuery = overridesFromQuery(router.query)
    setUrlOverrides((prev) => {
      const hasChanges = Object.entries(fromQuery).some(([key, value]) => prev[key as OptimizelyFlagKey] !== value)
      if (!hasChanges) return prev
      const next = { ...prev, ...fromQuery }
      persistOverrides(next)
      return next
    })
  }, [enabled, router.isReady, router.query])

  const setFlagOverride = (key: string, value: OptimizelyFlagValue) => {
    setDecisions((prev) => ({ ...prev, [key]: { value, isHoldback: false } }))
  }

  const contextValue = useMemo(
    () => ({ ...resolveFlags(decisions, urlOverrides), consentGiven: hasConsent, setFlagOverride }),
    [decisions, urlOverrides, hasConsent],
  )

  return <OptimizelyContext.Provider value={contextValue}>{children}</OptimizelyContext.Provider>
}
